import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { queueAPI } from '../../services/api';
import { obscureEmail, obscurePhone } from '../../utils/helpers';
import './WaiverModal.css';

const WaiverModal = ({ waiver, onClose }) => {
  const [iframeUrl, setIframeUrl] = useState('');
  const [completing, setCompleting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const { t } = useTranslation('queue');

  // Mark in_progress when modal opens
  useEffect(() => {
    queueAPI.updateQueueItem(waiver.queue_id, { status: 'in_progress' }).catch((err) => {
      console.error('[WaiverModal] Failed to set in_progress:', err);
    });
  }, [waiver.queue_id]);

  // Listen for postMessage from iframe when all waivers are completed
  const handleMessage = useCallback(async (event) => {
    console.log('[WaiverModal] postMessage received:', event.data);
    try {
      const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      if (data?.messageType === 'allWaiversCompleted') {
        console.log('[WaiverModal] allWaiversCompleted - updating queue item:', waiver.queue_id);
        setCompleting(true);
        try {
          await queueAPI.updateQueueItem(waiver.queue_id, { status: 'completed' });
          console.log('[WaiverModal] Queue item updated successfully');
        } catch (apiErr) {
          console.error('[WaiverModal] Failed to update queue item:', apiErr);
        }
        setShowThankYou(true);
      }
    } catch (parseErr) {
      // Ignore non-JSON or unrelated messages
    }
  }, [waiver.queue_id]);

  // Auto-dismiss thank you dialog after 4 seconds
  useEffect(() => {
    if (showThankYou) {
      const timer = setTimeout(() => {
        onClose();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showThankYou, onClose]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Revert to pending when closed without completing
  const handleAbandon = useCallback(async () => {
    try {
      await queueAPI.updateQueueItem(waiver.queue_id, { status: 'pending' });
    } catch (err) {
      console.error('[WaiverModal] Failed to revert to pending:', err);
    }
    onClose();
  }, [waiver.queue_id, onClose]);

  useEffect(() => {
    setIframeUrl(waiver.waiver_url || '');

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [waiver]);

  if (showThankYou) {
    return (
      <>
      <div className="waiver-modal-backdrop" />
      <div className="waiver-modal-overlay">
        <div className="thank-you-dialog">
          <div className="thank-you-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#00C853" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h2 className="thank-you-title">{t('modal.thankYou.title')}</h2>
          <p className="thank-you-message">
            {waiver.waivers > 1
              ? t('modal.thankYou.messageMultiple')
              : t('modal.thankYou.messageSingle')}
          </p>
          <button className="thank-you-btn" onClick={onClose}>
            {t('modal.thankYou.done')}
          </button>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
    <div className="waiver-modal-backdrop" />
    <div className="waiver-modal-overlay">
      <div className="waiver-modal-content">
        <div className="waiver-modal-header">
          <div className="modal-title">
            <div className="modal-title-row">
              <h2>{waiver.guest_name}</h2>
              {waiver.party_size > 1 && (
                <span className="modal-party-chip">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                  {t('partyOf', { count: waiver.party_size })}
                </span>
              )}
              <span className="modal-contact">{obscureEmail(waiver.guest_email) || obscurePhone(waiver.guest_phone)}</span>
            </div>
          </div>
          <button className="modal-close" onClick={handleAbandon}>
            ×
          </button>
        </div>

        <div className="waiver-modal-body">
          {iframeUrl ? (
            <iframe
              src={iframeUrl}
              title={t('modal.iframeTitle')}
              className="waiver-iframe"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            />
          ) : (
            <div className="iframe-loading">
              <div className="spinner-large"></div>
              <p>{t('modal.loading')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default WaiverModal;
